import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { ChevronLeft, ChevronRight, MapPin, Heart, ArrowUpRight, FileText, ShieldCheck } from "lucide-react";
import { api, resolveImage } from "../lib/api";
import { CATEGORIES, categoryById, priceLabelFor, slugify } from "../lib/data";
import CategoryIcon from "./CategoryIcon";

const PLACEHOLDER = "https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?crop=entropy&cs=srgb&fm=jpg&q=80&w=800";

// Distinct accent per category so each row feels alive but consistent.
const CATEGORY_THEMES = {
  properties: { chipBg: "bg-emerald-500", chipText: "text-white", iconBg: "bg-emerald-50", iconText: "text-emerald-600", price: "text-emerald-600" },
  vehicles: { chipBg: "bg-sky-500", chipText: "text-white", iconBg: "bg-sky-50", iconText: "text-sky-600", price: "text-sky-600" },
  businesses: { chipBg: "bg-violet-500", chipText: "text-white", iconBg: "bg-violet-50", iconText: "text-violet-600", price: "text-violet-600" },
  architects: { chipBg: "bg-amber-500", chipText: "text-white", iconBg: "bg-amber-50", iconText: "text-amber-600", price: "text-amber-600" },
  contractors: { chipBg: "bg-orange-500", chipText: "text-white", iconBg: "bg-orange-50", iconText: "text-orange-600", price: "text-orange-600" },
  "interior-designers": { chipBg: "bg-pink-500", chipText: "text-white", iconBg: "bg-pink-50", iconText: "text-pink-600", price: "text-pink-600" },
  "event-services": { chipBg: "bg-fuchsia-500", chipText: "text-white", iconBg: "bg-fuchsia-50", iconText: "text-fuchsia-600", price: "text-fuchsia-600" },
  "travel-services": { chipBg: "bg-cyan-500", chipText: "text-white", iconBg: "bg-cyan-50", iconText: "text-cyan-600", price: "text-cyan-600" },
  education: { chipBg: "bg-indigo-500", chipText: "text-white", iconBg: "bg-indigo-50", iconText: "text-indigo-600", price: "text-indigo-600" },
  healthcare: { chipBg: "bg-rose-500", chipText: "text-white", iconBg: "bg-rose-50", iconText: "text-rose-600", price: "text-rose-600" },
  food: { chipBg: "bg-red-500", chipText: "text-white", iconBg: "bg-red-50", iconText: "text-red-600", price: "text-red-600" },
  beauty: { chipBg: "bg-pink-500", chipText: "text-white", iconBg: "bg-pink-50", iconText: "text-pink-600", price: "text-pink-600" },
  fitness: { chipBg: "bg-lime-600", chipText: "text-white", iconBg: "bg-lime-50", iconText: "text-lime-700", price: "text-lime-700" },
  "ev-charging": { chipBg: "bg-teal-500", chipText: "text-white", iconBg: "bg-teal-50", iconText: "text-teal-600", price: "text-teal-600" },
};
const DEFAULT_THEME = { chipBg: "bg-emerald-500", chipText: "text-white", iconBg: "bg-slate-100", iconText: "text-slate-700", price: "text-emerald-600" };

function chipLabel(listing) {
  // Property "For Sale" / "For Rent" vs. subcategory tag
  const kd = listing.keyDetails || {};
  if (listing.category === "properties") {
    if ((kd.listing_type || "").toLowerCase() === "rent") return "For Rent";
    return "For Sale";
  }
  return listing.subcategory || (categoryById(listing.category)?.label || listing.category || "Listing");
}

function priceFragment(listing) {
  const kd = listing.keyDetails || {};
  const base = priceLabelFor(listing.price, listing.priceType);
  if (listing.category === "properties" && (kd.listing_type || "").toLowerCase() === "rent") {
    return { main: base, suffix: "/mo" };
  }
  return { main: base, suffix: "" };
}

function Card({ listing }) {
  const theme = CATEGORY_THEMES[listing.category] || DEFAULT_THEME;
  const img = resolveImage(listing.images && listing.images[0]) || PLACEHOLDER;
  const chip = chipLabel(listing);
  const price = priceFragment(listing);
  const href = `/listings/${listing.id}/${listing.slug || slugify(listing.title)}`;
  const isVerified = listing.isVerifiedSeller;

  return (
    <Link
      to={href}
      className={`group relative flex-none w-[220px] sm:w-[240px] md:w-[260px] bg-white rounded-2xl border overflow-hidden transition-all duration-300 hover:-translate-y-0.5 hover:shadow-[0_18px_40px_rgba(0,0,0,0.08)] snap-start ${
        isVerified ? "border-amber-200 bg-gradient-to-b from-amber-50/40 to-white shadow-[0_2px_10px_rgba(217,167,20,0.10)]" : "border-neutral-200 shadow-[0_2px_10px_rgba(0,0,0,0.04)]"
      }`}
      data-testid={`recent-card-${listing.id}`}
    >
      <div className="relative aspect-[4/3] bg-neutral-100 overflow-hidden">
        <img src={img} alt={listing.title} loading="lazy" className="absolute inset-0 h-full w-full object-cover group-hover:scale-105 transition-transform duration-500" />
        <span className={`absolute top-2.5 left-2.5 px-2 py-0.5 rounded text-[10px] font-semibold ${theme.chipBg} ${theme.chipText} shadow-sm`}>{chip}</span>
        {isVerified && (
          <span className="absolute top-2.5 right-10 inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded bg-amber-500/95 text-white text-[9px] font-bold uppercase tracking-wide shadow-sm">
            <ShieldCheck className="h-2.5 w-2.5" /> Verified
          </span>
        )}
        <button type="button" onClick={(e) => { e.preventDefault(); }} className="absolute top-2.5 right-2.5 h-7 w-7 rounded-full bg-white/95 backdrop-blur flex items-center justify-center shadow-sm hover:bg-white transition" aria-label="Save">
          <Heart className="h-3.5 w-3.5 text-neutral-700" />
        </button>
      </div>
      <div className="p-3">
        <h3 className="font-semibold text-sm text-ink line-clamp-1" title={listing.title}>{listing.title}</h3>
        <p className="mt-0.5 flex items-center gap-1 text-[11px] text-muted-foreground">
          <MapPin className="h-3 w-3 flex-none" />
          <span className="line-clamp-1">{[listing.area, listing.city].filter(Boolean).join(", ") || listing.city || "—"}</span>
        </p>
        <div className="mt-2 flex items-baseline gap-1">
          <span className={`text-[15px] font-bold ${theme.price} tabular-nums`}>{price.main}</span>
          {price.suffix && <span className="text-[10px] text-muted-foreground">{price.suffix}</span>}
        </div>
      </div>
    </Link>
  );
}

function EmptyCard({ categoryId, categoryLabel }) {
  return (
    <div className="flex-none w-[220px] sm:w-[240px] md:w-[260px] rounded-2xl border border-dashed border-neutral-300 bg-white/70 p-4 flex flex-col items-center justify-center text-center snap-start" data-testid={`recent-empty-${categoryId}`}>
      <div className="h-10 w-10 rounded-full bg-neutral-100 flex items-center justify-center text-neutral-500 mb-2">
        <FileText className="h-4 w-4" />
      </div>
      <p className="text-sm font-semibold text-ink">No listing yet</p>
      <p className="text-[11px] text-muted-foreground mt-0.5">Be the first to add a {categoryLabel} listing</p>
      <Link
        to="/dashboard/create-listing"
        className="mt-3 inline-flex items-center gap-1 text-[11px] font-semibold px-2.5 py-1.5 rounded-md bg-primary text-white hover:bg-primary/90 transition"
        data-testid={`recent-empty-cta-${categoryId}`}
      >
        Post Free Listing
      </Link>
    </div>
  );
}

function CategoryRow({ category, items }) {
  const scrollerRef = useRef(null);
  const [progress, setProgress] = useState(0);
  const theme = CATEGORY_THEMES[category.id] || DEFAULT_THEME;

  const scroll = (dir) => {
    const el = scrollerRef.current;
    if (!el) return;
    el.scrollBy({ left: dir * (el.clientWidth * 0.85), behavior: "smooth" });
  };

  const onScroll = () => {
    const el = scrollerRef.current;
    if (!el) return;
    const max = Math.max(1, el.scrollWidth - el.clientWidth);
    setProgress(Math.min(100, Math.max(0, (el.scrollLeft / max) * 100)));
  };

  useEffect(() => { onScroll(); }, []);

  return (
    <div className="mt-6 first:mt-0" data-testid={`recent-row-${category.id}`}>
      <div className="flex items-center justify-between gap-3 mb-3 px-1">
        <div className="flex items-center gap-2.5">
          <span className={`h-9 w-9 rounded-xl ${theme.iconBg} ${theme.iconText} flex items-center justify-center`}>
            <CategoryIcon name={category.icon} className="h-4 w-4" />
          </span>
          <h3 className="font-display font-semibold text-ink text-lg">{category.label}</h3>
        </div>
        <div className="hidden md:flex items-center gap-1">
          <button onClick={() => scroll(-1)} className="h-8 w-8 rounded-full border border-neutral-200 flex items-center justify-center text-neutral-600 hover:bg-neutral-50 transition" aria-label="Scroll left" data-testid={`recent-prev-${category.id}`}>
            <ChevronLeft className="h-4 w-4" />
          </button>
          <button onClick={() => scroll(1)} className="h-8 w-8 rounded-full border border-neutral-200 flex items-center justify-center text-neutral-600 hover:bg-neutral-50 transition" aria-label="Scroll right" data-testid={`recent-next-${category.id}`}>
            <ChevronRight className="h-4 w-4" />
          </button>
          <Link to={`/listings/${category.id}`} className="ml-1 text-xs font-semibold text-accent hover:underline inline-flex items-center gap-0.5" data-testid={`recent-viewall-${category.id}`}>
            View all <ArrowUpRight className="h-3 w-3" />
          </Link>
        </div>
      </div>
      <div className="relative">
        <div
          ref={scrollerRef}
          onScroll={onScroll}
          className="flex gap-3 overflow-x-auto no-scrollbar snap-x snap-mandatory scroll-smooth pb-3 -mx-1 px-1"
          style={{ scrollbarWidth: "none" }}
        >
          {items.length > 0 ? items.map((l) => <Card key={l.id} listing={l} />) : <EmptyCard categoryId={category.id} categoryLabel={category.label.toLowerCase()} />}
        </div>
      </div>
      {/* Scroll progress bar (matches reference) */}
      <div className="mx-1 mt-1 h-1 rounded-full bg-neutral-100 overflow-hidden">
        <div className={`h-full ${theme.chipBg} transition-[width] duration-200`} style={{ width: `${Math.max(15, progress || 15)}%` }} />
      </div>
    </div>
  );
}

export default function RecentListingsSlider() {
  const [byCategory, setByCategory] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    api.get("/listings", { params: { sort: "newest", limit: 60 } })
      .then((r) => {
        if (!alive) return;
        const grouped = {};
        for (const it of r.data.items || []) {
          const c = it.category || "other";
          (grouped[c] = grouped[c] || []).push(it);
        }
        setByCategory(grouped);
      })
      .catch(() => setByCategory({}))
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, []);

  // Order: categories with listings first (in CATEGORIES order), then a small
  // set of always-visible categories with an empty-card placeholder.
  const rows = useMemo(() => {
    const withData = CATEGORIES.filter((c) => (byCategory[c.id] || []).length > 0);
    if (withData.length > 0) return withData;
    // Empty first-run: show top 4 canonical categories with empty placeholders
    const fallback = ["properties", "vehicles", "businesses", "food"];
    return CATEGORIES.filter((c) => fallback.includes(c.id));
  }, [byCategory]);

  return (
    <section className="max-w-7xl mx-auto px-4 sm:px-6 py-10 md:py-14" data-testid="recent-slider">
      <div className="flex items-end justify-between gap-4 mb-2">
        <div>
          <h2 className="font-display font-bold text-2xl md:text-3xl text-ink">Recent Listings</h2>
          <p className="text-muted-foreground text-sm mt-1">Explore the latest properties and businesses near you</p>
        </div>
        <Link to="/listings" className="hidden md:inline-flex items-center gap-1 text-sm font-medium px-3 py-1.5 rounded-lg border border-neutral-200 hover:border-neutral-400 hover:bg-white transition" data-testid="recent-view-all">
          View All
        </Link>
      </div>
      <div className="mt-4">
        {loading ? (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {[0, 1, 2, 3].map((i) => (
              <div key={i} className="rounded-2xl border border-neutral-200 bg-white h-[240px] animate-pulse" />
            ))}
          </div>
        ) : (
          rows.map((c) => (
            <CategoryRow key={c.id} category={c} items={byCategory[c.id] || []} />
          ))
        )}
      </div>
    </section>
  );
}
